import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const SIM_DEFAULT = { currency: "CAD", impressions: 0, ctr: 0.5, conv_rate: 2, avg_basket: 0, commission_rate: 0, commission_per_conversion: 0,
  fixed_price: 0, cpm: 0, cpc: 0, partner_margin: null, mpl_costs: 0 };
const FIELDS = [["impressions", "Impressions visibles (hypothèse)", 1], ["ctr", "Taux de clic (%)", 0.1], ["conv_rate", "Taux de conversion après clic (%)", 0.1],
  ["avg_basket", "Panier moyen", 0.01], ["partner_margin", "Marge du partenaire (%) — facultatif", 0.1], ["fixed_price", "Prix fixe", 0.01], ["cpm", "Prix par mille impressions", 0.01],
  ["cpc", "Prix par clic", 0.01], ["commission_rate", "Commission sur ventes (%)", 0.1], ["commission_per_conversion", "Commission par conversion", 0.01], ["mpl_costs", "Coûts internes estimés", 0.01]];
const money = (v, c) => (v == null ? "—" : `${Number(v).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`);

export function SimInputs({ v, set, lockCurrency, hide = [] }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3" data-testid="sim-inputs">
      {!lockCurrency && (
        <label className="text-[11px] text-slate-400">Devise
          <select className="mpl-input mt-1" value={v.currency} onChange={(e) => set({ ...v, currency: e.target.value })} data-testid="sim-currency">{["CAD", "EUR", "USD"].map((c) => <option key={c}>{c}</option>)}</select>
        </label>)}
      {FIELDS.filter(([k]) => !hide.includes(k)).map(([k, l, step]) => (
        <label key={k} className="text-[11px] text-slate-400">{l}
          <input type="number" min="0" step={step} className="mpl-input mt-1" value={v[k] ?? ""} data-testid={`sim-${k}`}
            onChange={(e) => set({ ...v, [k]: e.target.value === "" ? (k === "partner_margin" ? null : 0) : Number(e.target.value) })} />
        </label>))}
    </div>
  );
}

const fr = (v) => (v == null ? "—" : Number(v).toLocaleString("fr-CA"));

export function SimResult({ r }) {
  if (!r) return null;
  const c = r.currency;
  const rows = [["Clics simulés", fr(r.clicks)], ["Conversions simulées", fr(r.conversions)], ["Ventes simulées pour le partenaire", money(r.partner_sales, c)],
    ["Coût total pour le partenaire", money(r.partner_cost, c)], ["Coût par conversion", money(r.partner_cost_per_conversion, c)], ["Ventes / coût (ROAS)", fr(r.partner_roas)],
    ["Marge brute du partenaire", money(r.partner_gross_margin, c)], ["Résultat net du partenaire", money(r.partner_net, c)],
    ["Seuil de rentabilité (conversions)", fr(r.break_even_conversions)], ["Revenus média Ma Petite Ligue", money(r.mpl_media, c)],
    ["Commissions Ma Petite Ligue", money(r.mpl_commission, c)], ["Revenu total Ma Petite Ligue", money(r.mpl_revenue, c)], ["Net après coûts internes", money(r.mpl_net, c)]];
  return (
    <div className="space-y-2" data-testid="sim-result">
      <p className="rounded-lg border border-amber-300/30 bg-amber-300/10 p-2 text-[11px] text-amber-100" data-testid="sim-disclaimer">SIMULATION — {r.disclaimer}</p>
      <dl className="grid gap-1.5 sm:grid-cols-2">{rows.map(([l, val]) => (
        <div key={l} className="flex justify-between gap-2 rounded-lg bg-white/[0.03] px-2 py-1.5 text-xs"><dt className="text-slate-400">{l}</dt><dd className="font-semibold text-white" data-testid={`sim-out-${l}`}>{val}</dd></div>))}</dl>
    </div>
  );
}

export function useSimulation(v) {
  const [r, setR] = useState(null);
  const key = v ? JSON.stringify(v) : "";
  useEffect(() => {
    if (!key) { setR(null); return undefined; }
    const t = setTimeout(() => { api.post("/superadmin/regie/simulate", JSON.parse(key)).then(({ data }) => setR(data)).catch((e) => toast.error(errMsg(e))); }, 350);
    return () => clearTimeout(t);
  }, [key]);
  return r;
}

export default function Simulator() {
  const [v, setV] = useState(SIM_DEFAULT);
  const r = useSimulation(v);
  return (
    <section className="mpl-card space-y-3 p-4" data-testid="simulator">
      <h3 className="flex items-center gap-2 text-sm font-bold"><Calculator size={15} /> Simulateur de revenus</h3>
      <p className="text-[11px] text-slate-400">Toutes les valeurs sont des hypothèses que tu saisis. Rien ici n'est un résultat mesuré ni une promesse faite au partenaire.</p>
      <SimInputs v={v} set={setV} />
      <SimResult r={r} />
    </section>
  );
}
